import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Euro,
  Phone,
  MessageCircle, 
  X, 
  Home, 
  HeartPulse, 
  Sparkles, 
  Landmark, 
  Briefcase, 
  Package,
  FileQuestion,   
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Helmet } from 'react-helmet-async';

// Import SVG illustrations
import RestaurantSVG from '../assets/Accountant-amico.svg';
import RetailSVG from '../assets/Medical-care-cuate.svg';
import HomeServicesSVG from '../assets/cleaning-service-amico.svg';
import ProfessionalSVG from '../assets/Law-firm-amico.svg';
import BeautySVG from '../assets/massage-amico.svg';
import LogisticsSVG from '../assets/Logistics-rafiki.svg';

const Examples = () => {
  const { t } = useTranslation();
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [activeExample, setActiveExample] = useState(null);

  const examples = [
    {
      key: 'accountant',
      title: t('examples.accountant.title'),
      phone: t('examples.accountant.phone'),
      icon: <Euro className="w-6 h-6" />,
      backgroundImage: RestaurantSVG,
      businessName: t('examples.accountant.businessName')
    },
    {
      key: 'elderCare',
      title: t('examples.elderCare.title'),
      phone: t('examples.elderCare.phone'),
      icon: <HeartPulse className="w-6 h-6" />,
      backgroundImage: RetailSVG,
      businessName: t('examples.elderCare.businessName')
    },
    {
      key: 'homeServices',
      title: t('examples.homeServices.title'),
      phone: t('examples.homeServices.phone'),
      icon: <Home className="w-6 h-6" />,
      backgroundImage: HomeServicesSVG,
      businessName: t('examples.homeServices.businessName')
    },
    {
      key: 'law',
      title: t('examples.law.title'),
      phone: t('examples.law.phone'),
      icon: <Landmark className="w-6 h-6" />,
      backgroundImage: ProfessionalSVG,
      businessName: t('examples.law.businessName')
    },
    {
      key: 'beauty',
      title: t('examples.beauty.title'),
      phone: t('examples.beauty.phone'),
      icon: <Sparkles className="w-6 h-6" />,
      backgroundImage: BeautySVG,
      businessName: t('examples.beauty.businessName')
    },
    {
      key: 'logistics',
      title: t('examples.logistics.title'),
      phone: t('examples.logistics.phone'),
      icon: <Package className="w-6 h-6" />,
      backgroundImage: LogisticsSVG,
      businessName: t('examples.logistics.businessName')
    }
  ];

  const handleDescription = (example) => {
    setActiveExample(example);
    setIsDescriptionOpen(true);
  };


  return (
    <section className="py-12 bg-gradient-to-b from-gray-50 to-gray-100">

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
            {t('examples.heading')}
          </h2>
          <p className="text-lg text-gray-600 mt-4">
            {t('examples.description')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {examples.map((example, index) => (
            <div 
              key={index}
              className="relative group rounded-xl overflow-hidden transform transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
            >
              <div className="relative h-60 w-full rounded-xl bg-white">
                {/* SVG Background */}
                <img 
                  src={example.backgroundImage}
                  alt={example.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/60 group-hover:from-black/40 group-hover:to-black/80 transition-all duration-300" />
                
                {/* Content */}
                <div className="absolute inset-0 p-6 flex flex-col justify-between text-white">
                  <div className="text-center transform transition-all duration-300 group-hover:-translate-y-2">
                    <div className="bg-white/10 backdrop-blur-sm rounded-full p-3 w-fit mx-auto mb-4">
                      {example.icon}
                    </div>
                    <h3 className="text-2xl font-bold mb-2">{example.title}</h3>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex justify-center space-x-4 opacity-0 transform translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                    <button
                      onClick={() => handleDescription(example)}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-full flex items-center transition-colors duration-300"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      {t('examples.actions.view_description')}
                    </button>

                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Description Dialog */}
<Dialog open={isDescriptionOpen} onOpenChange={setIsDescriptionOpen}>
  <DialogContent className="sm:max-w-2xl bg-white rounded-2xl p-0 max-h-[90vh] flex flex-col">

    {/* Header Section */}
    <div className="sticky top-0 bg-white z-10 border-b">
      <DialogHeader className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {activeExample?.icon && (
              <div className="p-3 rounded-full bg-gradient-to-r from-blue-50 to-indigo-50">
                {activeExample.icon}
              </div>
            )}
            <DialogTitle className="text-2xl font-bold text-gray-900">
              {activeExample?.businessName}
            </DialogTitle>
          </div>
          <button
            onClick={() => setIsDescriptionOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </DialogHeader>
    </div>

    {/* Content Section */}
    <div className="flex-1 overflow-y-auto">
    {activeExample && (
        <>
          {/* Client Problem Section */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
            <h3 className="flex items-center text-lg font-semibold text-gray-900 mb-3">
              <FileQuestion  className="w-5 h-5 mr-2 text-blue-600" />
              {t('examples.usecase.client_problem_title')}
            </h3>
            <p className="text-gray-700 leading-relaxed">
              {t(`examples.${activeExample.key}.client_problem`)}
            </p>
          </div>

          {/* Agent Role Section */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6">
            <h3 className="flex items-center text-lg font-semibold text-gray-900 mb-3">
              <Briefcase className="w-5 h-5 mr-2 text-purple-600" />
              {t('examples.usecase.agent_role_title')}
            </h3>
            <p className="text-gray-700 leading-relaxed">
              {t(`examples.${activeExample.key}.agent_role`)}
            </p>
          </div>

          {/* Information Gathering Section */}
          <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-xl p-6">
            <h3 className="flex items-center text-lg font-semibold text-gray-900 mb-3">
              <MessageCircle className="w-5 h-5 mr-2 text-green-600" />
              {t('examples.usecase.information_gathering_title')}
            </h3>
            <p className="text-gray-700 leading-relaxed">
              {t(`examples.${activeExample.key}.information_gathering`)}
            </p>
          </div>

          {/* Solution Exploration Section */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6">
            <h3 className="flex items-center text-lg font-semibold text-gray-900 mb-3">
              <Sparkles className="w-5 h-5 mr-2 text-yellow-600" />
              {t('examples.usecase.solution_exploration_title')}
            </h3>
            <p className="text-gray-700 leading-relaxed">
              {t(`examples.${activeExample.key}.solution_exploration`)}
            </p>
          </div>

          {/* Appointment Setting Section */}
          <div className="bg-gradient-to-r from-red-50 to-rose-50 rounded-xl p-6">
            <h3 className="flex items-center text-lg font-semibold text-gray-900 mb-3">
              <Phone className="w-5 h-5 mr-2 text-red-600" />
              {t('examples.usecase.appointment_setting_title')}
            </h3>
            <p className="text-gray-700 leading-relaxed">
              {t(`examples.${activeExample.key}.appointment_setting`)}
            </p>
          </div>
        </>
      )}
    </div>
  </DialogContent>
</Dialog>


        {/* Chat Dialog */}
        <Dialog open={isChatOpen} onOpenChange={setIsChatOpen}>
          <DialogContent className="sm:max-w-md h-[600px]">
            <DialogHeader>
              <DialogTitle>{activeExample?.businessName}</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col h-full">
              <div className="flex-1 overflow-y-auto p-4">
                <div className="bg-gray-100 rounded-lg p-3 mb-4 max-w-[80%]">
                  <p>
                    {t('examples.actions.chat_welcome', { business: activeExample?.businessName })}
                  </p>
                </div>
              </div>
              <div className="border-t p-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    placeholder={t('examples.actions.chat_placeholder')}
                    className="flex-1 border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-300">
                    <MessageCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default Examples;
